<template>
  <div
    class="d-flex flex-column py-4"
    style="gap: 24px; max-height: 400px; overflow-y: scroll; font-size: 1.05rem"
  >
    <div v-if="formattedData?.length === 0" class="p-4">
      <h6>Es gibt noch keine Änderungen zu dieser Aufgabe.</h6>
    </div>
    <div v-for="(change, i) in formattedData" :key="change.key + '-' + change.date + '-' + i" class="">
      <div class="d-flex">
        <span v-if="change.key === 'Datei'">
          {{ change.nameAenderer }} hat {{ change.formattedKey }}
          <b>{{ change.fileName }}</b>
          {{ change.fileAction }}.
        </span>
        <!-- Verlinkungs-Eintrag -->
        <span v-else-if="change.isVerlinkung" class="d-flex align-items-center flex-wrap">
          {{ change.verlinkungText }}:
          <span v-for="(wert, idx) in change.verlinkungValues" :key="idx" class="d-inline-flex">
            <span class="kachel-badge gray ml-1">{{ wert }}</span>
            <span v-if="idx < change.verlinkungValues.length - 1">,</span>
          </span>
        </span>
        <!-- Leistung wurde hinzugefügt oder entfernt -->
        <span v-else-if="change.key === 'Leistung'" class="d-flex align-items-center flex-wrap">
          Es wurde die Leistung
          <span class="kachel-badge gray ml-1 mr-1">{{ change.leistungWert }}</span>
          {{ change.leistungAktion }}.
        </span>
        <!-- Automatisch ausgelöste Beschreibungsänderung ohne Person -->
        <span v-else-if="change.istAutomatischeBeschreibung">
          Es wurde
          <b>die Beschreibung</b>
          geändert.
        </span>
        <span v-else>
          {{ change.nameAenderer }} hat
          <b>{{ change.formattedKey }}</b>
          geändert.
        </span>
        <div
          style="color: rgba(0, 0, 0, 0.5); cursor: pointer; align-content: center"
          class="ml-6"
          :id="'date-change' + uniqueId + '-' + change.date + '-' + i"
        >
          {{ change.formattedDate }}
        </div>
        <b-tooltip
          :target="'date-change' + uniqueId + '-' + change.date + '-' + i"
          triggers="hover"
          placement="right"
        >
          <span style="font-size: 1rem">{{ formatDate(change.date) }}</span>
        </b-tooltip>
      </div>
      <div
        v-if="change.key !== 'Datei' && change.key !== 'Leistung' && !change.isVerlinkung"
        class="d-flex p-2 align-items-center"
        style="gap: 12px"
      >
        <span
          :class="['align-items-center', change.key === 'Beschreibung' ? 'beschreibung-content' : '']"
          :id="'tooltip-beschreibung-old' + uniqueId + '-' + change.date"
          v-html="change.oldValueFormatted"
        ></span>
        <span style="font-size: 20px">&rarr;</span>
        <span
          :class="['align-items-center', change.key === 'Beschreibung' ? 'beschreibung-content' : '']"
          :id="'tooltip-beschreibung-new' + uniqueId + '-' + change.date"
          v-html="change.newValueFormatted"
        ></span>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import { format, formatDistanceToNow, isAfter } from 'date-fns';
import de from 'date-fns/locale/de';
import { statusFormatterHtml, prioritaetFormatterHtml, parseFullUsername } from '@/core/common/helpers/utils';

export default {
  props: {
    data: { type: Object },
    // Erstellungszeitpunkt der Aufgabe
    erstellungsdatum: { type: String, default: null },
  },
  data() {
    return {
      ignoredFields: ['LastModifiedUserId', 'StatusErledigtDatum', 'StatusInArbeitDatum'],
      verlinkungKeys: ['Reisetermin', 'Vorgang', 'Reise'],
      verlinkungLabels: {
        Reisetermin: { singular: 'ein Reisetermin', plural: 'Reisetermine' },
        Vorgang: { singular: 'ein Vorgang', plural: 'Vorgänge' },
        Reise: { singular: 'eine Reise', plural: 'Reisen' },
      },
      formatKeys: {
        Status: 'den Status',
        Prioritaet: 'die Priorität',
        Team: 'das Team',
        Bearbeiter: 'die zugewiesene Person',
        Beschreibung: 'die Beschreibung',
        Ergebnis: 'das Ergebnis',
        ErgebnisBeschreibung: 'die Ergebnisbeschreibung',
        Faelligkeitsdatum: 'das Fälligkeitsdatum',
        Beobachter: 'den Beobachter',
        Datei: 'die Datei',
      },
      statusFormatterHtml,
      prioritaetFormatterHtml,
      uniqueId: Math.random().toString(36).substring(2, 9),
    };
  },
  computed: {
    ...mapGetters(['getAllKategorien']),
    ...mapState({
      users: state => state.users['users'],
    }),
    formattedData() {
      return Object.entries(this.data)
        .reduce((acc, curr) => {
          const changeDate = curr[0];
          const { changes, lastModifiedUserId } = curr[1];

          const teamBearbeiterChangesWithNull = changes.filter(
            change =>
              ['Team', 'Bearbeiter'].includes(change.key) &&
              (change.newValue == null || change.oldValue == null)
          );

          if (teamBearbeiterChangesWithNull.length === 2) {
            const newValueObject = teamBearbeiterChangesWithNull.find(
              change => change.newValue != null && ['Team', 'Bearbeiter'].includes(change.key)
            );
            const oldValueObject = teamBearbeiterChangesWithNull.find(
              change => change.oldValue != null && ['Team', 'Bearbeiter'].includes(change.key)
            );
            const key = 'die zugewiesene Person/Team';
            const aendererObject = this.users.find(user => user.id === lastModifiedUserId);
            const nameAenderer = aendererObject ? parseFullUsername(aendererObject) : '-';
            const newValueFormatted = this.getValueFormatted(newValueObject.newValue, newValueObject.key);
            const oldValueFormatted = this.getValueFormatted(oldValueObject.oldValue, oldValueObject.key);
            const formattedDate = this.getDateDistanceToNow(changeDate);
            acc.push({
              oldValue: oldValueObject.oldValue,
              oldValueFormatted,
              newValue: newValueObject.newValue,
              newValueFormatted,
              nameAenderer,
              date: changeDate,
              formattedDate,
              key,
              formattedKey: key,
            });
          }

          for (const change of changes) {
            if (this.verlinkungKeys.includes(change.key)) {
              continue;
            }
            if (
              this.ignoredFields.includes(change.key) ||
              (change.newValue == null && change.key !== 'Datei' && change.key !== 'Leistung') ||
              (change.oldValue == null && ['Team', 'Bearbeiter'].includes(change.key))
            ) {
              continue;
            }

            const { newValue, oldValue, key, additionalIdentifier } = change;
            const aendererObject = this.users.find(user => user.id === lastModifiedUserId);
            const nameAenderer = aendererObject ? parseFullUsername(aendererObject) : '-';
            const formattedDate = this.getDateDistanceToNow(changeDate);

            const newValueFormatted = this.getValueFormatted(newValue, key);
            const oldValueFormatted = this.getValueFormatted(oldValue, key);

            let fileName = null;
            let fileAction = null;
            if (key === 'Datei') {
              if (newValue) {
                fileName = newValue;
                fileAction = 'hochgeladen';
              } else if (oldValue) {
                fileName = oldValue;
                fileAction = 'entfernt';
              }
            }

            // Bei Leistungen wird ein sprechender Satz mit dem Wert als Badge statt der Alt/Neu-Gegenüberstellung angezeigt
            let leistungAktion = null;
            let leistungWert = null;
            if (key === 'Leistung') {
              leistungAktion = newValue != null ? 'hinzugefügt' : 'entfernt';
              leistungWert = newValue != null ? newValue : oldValue;
            }

            // Eine Beschreibungsänderung ohne additionalIdentifier wurde automatisch, nicht durch eine Person, ausgelöst
            const istAutomatischeBeschreibung = key === 'Beschreibung' && additionalIdentifier == null;

            acc.push({
              oldValue,
              oldValueFormatted,
              newValue,
              newValueFormatted,
              nameAenderer,
              date: changeDate,
              formattedDate,
              key,
              formattedKey: this.formatKeys[key],
              fileName,
              fileAction,
              additionalIdentifier,
              leistungAktion,
              leistungWert,
              istAutomatischeBeschreibung,
            });
          }

          // Verlinkungen, die bereits beim Erstellen der Aufgabe gesetzt wurden haben den selben zeitstempel, wie die aufgabe und werden ausgeblendet
          const istManuelleVerlinkungsaenderung = !this.istErstellungszeitpunkt(changeDate);

          // Der Core liefert füre jede geänderte Verlinkung einen einzelnen Eintrag
          // Für die Anzeige des Verlaufs werden sie hier nach Typ + Aktion gebündelt
          const verlinkungGruppen = {};
          for (const change of changes) {
            if (!this.verlinkungKeys.includes(change.key) || !istManuelleVerlinkungsaenderung) continue;

            const aktion = change.newValue != null ? 'hinzugefügt' : 'gelöscht';
            const wert = change.newValue != null ? change.newValue : change.oldValue;
            if (wert == null) continue;

            const gruppenKey = change.key + '-' + aktion;
            if (!verlinkungGruppen[gruppenKey]) {
              verlinkungGruppen[gruppenKey] = { key: change.key, aktion, values: [] };
            }
            verlinkungGruppen[gruppenKey].values.push(wert);
          }

          // Aus jeder gebündelten Gruppe genau einen anzeigefertigen Verlauf-Eintrag bauen
          for (const gruppe of Object.values(verlinkungGruppen)) {
            const labels = this.verlinkungLabels[gruppe.key];
            // Bei mehr als einem Wert die Mehrzahl-Formulierung verwenden
            const istMehrzahl = gruppe.values.length > 1;
            const verb = istMehrzahl ? 'Es wurden' : 'Es wurde';
            const label = istMehrzahl ? labels.plural : labels.singular;
            acc.push({
              date: changeDate,
              formattedDate: this.getDateDistanceToNow(changeDate),
              key: gruppe.key,
              isVerlinkung: true,
              verlinkungText: `${verb} ${label} ${gruppe.aktion}`,
              verlinkungValues: gruppe.values,
            });
          }
          return acc;
        }, [])
        .sort((a, b) => (isAfter(new Date(b.date), new Date(a.date)) ? 1 : -1));
    },
  },
  methods: {
    istErstellungszeitpunkt(dateString) {
      if (!this.erstellungsdatum) return false;
      const differenzInMs = Math.abs(new Date(dateString) - new Date(this.erstellungsdatum));
      return differenzInMs < 2000;
    },
    getDateDistanceToNow(dateString) {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: de });
    },
    formatDate(value) {
      return format(new Date(value), 'dd.MM.yyyy  HH:mm') + ' Uhr';
    },
    getValueFormatted(value, key) {
      if (key === 'Status') {
        return this.statusFormatterHtml[value];
      }

      if (key === 'Prioritaet') {
        return this.prioritaetFormatterHtml[value];
      }

      if (key === 'Ergebnis') {
        let ergebnisFormatted = '-';
        if (this.getAllKategorien) {
          // Search through all categories to find the ergebnis label
          for (const kategorie of this.getAllKategorien) {
            if (kategorie.ergebnisKategorienOptions) {
              const ergebnisOption = kategorie.ergebnisKategorienOptions.find(
                option => option.ergebnisKategorie === value
              );
              if (ergebnisOption) {
                ergebnisFormatted = ergebnisOption.labelFrontend;
                break;
              }
            }
          }
        }
        return '<span class="kachel-badge gray">' + ergebnisFormatted + '</span>';
      }

      if (key === 'Faelligkeitsdatum') {
        return '<span class="kachel-badge gray">' + format(new Date(value), 'dd.MM.yyyy') + '</span>';
      }

      if (key === 'Bearbeiter') {
        const personObject = this.users.find(user => user.id === value);
        const name = personObject ? parseFullUsername(personObject) : '-';
        return '<span class="kachel-badge gray">' + name + '</span>';
      }

      if (key === 'Beobachter') {
        if (value === '[]') return '---';
        const allBeobachter = this.users
          .filter(user => value.includes(user.id))
          .map(person => parseFullUsername(person))
          .join(', ');
        return allBeobachter;
      }

      if (key === 'Team') {
        return '<span class="kachel-badge gray">' + value + '</span>';
      }

      if (key === 'ErgebnisBeschreibung') {
        const valueFormatted = value ?? '-';
        return '<span class="kachel-badge gray">' + valueFormatted + '</span>';
      }

      return value;
    },
  },
};
</script>
<style scoped>
:deep(.kachel-badge) {
  height: auto;
  min-width: 0;
  padding: 6px 8px;
  font-size: 1rem;
  font-weight: 500;
  box-shadow: none;
}

:deep(p) {
  padding: 0;
  margin-bottom: 0;
}

:deep(.beschreibung-content table) {
  display: flex;
  gap: 4px;
  flex-direction: column;
  border-collapse: collapse;
  width: 100%;
}

:deep(.beschreibung-content th),
:deep(.beschreibung-content td) {
  border: 1px solid rgba(61, 37, 20, 0.12);
  padding: 6px 8px;
  vertical-align: top;
  word-break: break-word;
}

:deep(.beschreibung-content th) {
  background: #f9f9f9;
  font-weight: bold;
}
</style>
